import { useEffect, useRef, useState } from "react";
import { http, formatErr, API } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { Upload, RefreshCw, Database, ShoppingBag, Truck, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const PROVIDERS = [
  { key: "roposo_clout", label: "Roposo Clout", desc: "Source viral, trending Indian products with one tap.", icon: ShoppingBag, fields: ["api_key", "api_secret"] },
  { key: "dropdash", label: "DropDash", desc: "Curated dropshipping catalog with fast India fulfillment.", icon: Truck, fields: ["api_key"] },
  { key: "spocket", label: "Spocket", desc: "Global dropshipping suppliers (US/EU/Asia).", icon: Database, fields: ["api_key"] },
];

export default function AdminSourcing() {
  const fileRef = useRef();
  const [csvBusy, setCsvBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [settings, setSettings] = useState({});

  const load = () => http.get("/admin/settings").then((r) => setSettings(r.data));
  useEffect(() => { load(); }, []);

  const uploadCsv = async (file) => {
    setCsvBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("bls_admin_token");
      const res = await fetch(`${API}/admin/sourcing/csv`, {
        method: "POST",
        body: fd,
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload failed");
      setResult(data);
      toast.success(`Imported ${data.created} products (skipped ${data.skipped})`);
    } catch (e) {
      toast.error(e.message || "CSV upload failed");
    } finally {
      setCsvBusy(false);
    }
  };

  const connect = async (provider, fields) => {
    try {
      await http.post(`/admin/sourcing/connect/${provider}`, fields);
      toast.success(`${provider} connected`);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const sync = async (provider) => {
    try {
      const { data } = await http.post(`/admin/sourcing/sync/${provider}`);
      toast.info(data.message);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <div>
      <h1 className="font-display text-3xl font-bold">Product Sourcing</h1>
      <p className="text-white/45 text-sm mt-1">Import products via CSV or connect to popular sourcing platforms.</p>

      {/* CSV */}
      <div className="mt-8 bls-admin-card p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#2a9d6f]/15 text-[#2a9d6f] flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-display font-bold text-lg">CSV upload</h2>
              <p className="text-xs text-white/55">Columns: name, slug, price, compare_at_price, description, short_description, image, category, stock, sku, badge</p>
            </div>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && uploadCsv(e.target.files[0])}
            data-testid={TID.adminCsvUpload}
          />
          <button onClick={() => fileRef.current?.click()} disabled={csvBusy} className="bls-admin-btn">
            <Upload className="w-4 h-4" /> {csvBusy ? "Uploading…" : "Upload CSV"}
          </button>
        </div>
        {result && (
          <div className="mt-4 p-4 rounded-lg bg-[#16161f] text-sm font-mono space-y-1">
            <p>Created: <span className="text-[#2a9d6f]">{result.created}</span></p>
            <p>Skipped: <span className="text-yellow-400">{result.skipped}</span></p>
            {result.errors?.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer text-red-400">{result.errors.length} errors</summary>
                <ul className="mt-2 ml-4 list-disc text-xs text-white/70">
                  {result.errors.map((er, i) => <li key={i}>{er}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>

      {/* Connectors */}
      <h2 className="mt-10 font-display text-xl font-bold">Connectors</h2>
      <p className="text-white/45 text-sm mt-1">Add API credentials to enable a sync. Your keys are stored securely.</p>

      <div className="mt-4 grid lg:grid-cols-3 gap-6">
        {PROVIDERS.map((p) => {
          const cfg = settings[p.key] || {};
          return <Connector key={p.key} p={p} cfg={cfg} onConnect={(v) => connect(p.key, v)} onSync={() => sync(p.key)} />;
        })}
      </div>
    </div>
  );
}

function Connector({ p, cfg, onConnect, onSync }) {
  const [vals, setVals] = useState({});
  useEffect(() => { setVals({}); }, [cfg.enabled]);

  return (
    <div className="bls-admin-card p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#7f77dd]/15 text-[#7f77dd] flex items-center justify-center">
            <p.icon className="w-4 h-4" />
          </div>
          <div>
            <p className="font-display font-bold">{p.label}</p>
            <p className="text-xs text-white/45">{p.desc}</p>
          </div>
        </div>
        <span className={`bls-status ${cfg.enabled ? "bls-status-paid" : "bls-status-failed"}`}>
          {cfg.enabled ? "Connected" : "Not connected"}
        </span>
      </div>
      <div className="mt-4 space-y-2">
        {p.fields.map((f) => (
          <input
            key={f}
            value={vals[f] ?? cfg[f] ?? ""}
            onChange={(e) => setVals({ ...vals, [f]: e.target.value })}
            placeholder={f.replace("_", " ")}
            className="bls-admin-input"
            data-testid={`sourcing-${p.key}-${f}`}
          />
        ))}
      </div>
      <div className="mt-4 flex gap-2">
        <button
          onClick={() => onConnect(p.fields.reduce((acc, f) => ({ ...acc, [f]: vals[f] ?? cfg[f] ?? "" }), {}))}
          className="bls-admin-btn flex-1"
          data-testid={`${TID.adminSourcingConnect}-${p.key}`}
        >
          {cfg.enabled ? "Update" : "Connect"}
        </button>
        {cfg.enabled && (
          <button onClick={onSync} className="bls-admin-btn-ghost">
            <RefreshCw className="w-3 h-3" /> Sync
          </button>
        )}
      </div>
    </div>
  );
}
